var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isEmpty } from '../_utils/function';
import { useMount, useUpdate } from '../_utils/hooks';
import { isIOS } from '../_utils/platform';
import { createPrefixCls, substring } from '../_utils/string';
import { Icon } from '../icon';
import { convertType, convertValue } from './utils';
const prefixCls = createPrefixCls('input');
export const Input = (_a) => {
    var { className, wrapClassName, wrapStyle, defaultValue, value, autoFocus, formValue, type = 'text', maxLength, autoComplete = 'off', allowClear, filter, onChange, onBlur, setFormValue, onOverMaxLength, onClear } = _a, restProps = __rest(_a, ["className", "wrapClassName", "wrapStyle", "defaultValue", "value", "autoFocus", "formValue", "type", "maxLength", "autoComplete", "allowClear", "filter", "onChange", "onBlur", "setFormValue", "onOverMaxLength", "onClear"]);
    const defaultValueRef = useRef(defaultValue);
    const getPropsValue = useMemo(() => {
        if (!isEmpty(value)) {
            return value;
        }
        else if (!isEmpty(formValue)) {
            return formValue;
        }
        else {
            return undefined;
        }
    }, [formValue, value]);
    const [inputValue, setInputValue] = useState(getPropsValue === undefined
        ? isEmpty(defaultValueRef.current)
            ? ''
            : defaultValueRef.current
        : getPropsValue);
    const isOnComposition = useRef(false);
    const fixInputValueTimer = useRef();
    const id = useRef(String(Math.random()));
    const inputRef = useRef();
    useMount(() => {
        if (setFormValue) {
            if (!isEmpty(value)) {
                setFormValue(value, false);
            }
            else if (!isEmpty(defaultValueRef.current)) {
                setFormValue(defaultValueRef.current, false);
            }
        }
    });
    // value更新
    useUpdate(() => {
        setInputValue(convertValue(value));
        setFormValue && setFormValue(convertValue(value), false);
    }, [value]);
    // formValue更新，一般用来做重置操作，值为undefined
    useUpdate(() => {
        setInputValue(convertValue(formValue));
    }, [formValue]);
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus, inputRef]);
    const setFixedInputValue = useCallback((value) => {
        setInputValue(value);
        // 解决iOS9输入一个字出现2个字的问题
        if (isIOS()) {
            clearTimeout(fixInputValueTimer.current);
            fixInputValueTimer.current = window.setTimeout(() => {
                setInputValue(value);
            }, 10);
        }
    }, []);
    const handleChange = useCallback((e) => {
        e.persist && e.persist();
        const target = e.target;
        let value = target.value;
        setFixedInputValue(value);
        if (!isOnComposition.current) {
            if (maxLength && value.length > maxLength) {
                onOverMaxLength && onOverMaxLength();
                value = substring(value, maxLength);
                setFixedInputValue(value);
            }
            if (filter) {
                value = filter(value);
                setFixedInputValue(value);
            }
            // const inputType = convertType(type);
            // 修复 IOS 光标跳转问题
            // if (!(['number', 'file'].indexOf(inputType) >= 0)) {
            //   const pos = target.selectionEnd;
            //   setTimeout(() => {
            //     target && (target.selectionStart = pos);
            //     target && (target.selectionEnd = pos);
            //   });
            // }
            setTimeout(() => {
                onChange && onChange(e);
                setFormValue && setFormValue(value);
            });
        }
    }, [filter, maxLength, onChange, onOverMaxLength, setFixedInputValue, setFormValue]);
    const handleBlur = useCallback((e) => {
        if (isIOS()) {
            setTimeout(() => {
                /* 解决iOS微信input失去焦点页面不回弹的问题 */
                if (window.scrollBy) {
                    window.scrollBy(0, 0);
                }
                else if (document.activeElement) {
                    document.activeElement.scrollIntoView(true);
                }
            }, 10);
            /* 解决ios微信使用原生输入法点击系统提示的内容不触发onChange事件的问题 */
            handleChange(e);
        }
        onBlur && onBlur(e);
    }, [handleChange, onBlur]);
    const handleComposition = useCallback((e) => {
        if (e.type === 'compositionend') {
            isOnComposition.current = false;
            handleChange(e);
        }
        else {
            isOnComposition.current = true;
        }
    }, [handleChange]);
    const handleClear = useCallback(() => {
        onClear && onClear();
        setFixedInputValue('');
        setFormValue && setFormValue(undefined);
    }, [onClear, setFixedInputValue, setFormValue]);
    const inputType = useMemo(() => {
        return convertType(type);
    }, [type]);
    const inputCls = useMemo(() => {
        return classnames([
            prefixCls(),
            {
                [prefixCls('clear-input')]: allowClear
            },
            className
        ]);
    }, [allowClear, className]);
    const inputWrapCls = useMemo(() => {
        return classnames([prefixCls('wrapper'), wrapClassName]);
    }, [wrapClassName]);
    const clearIconCls = useMemo(() => {
        return classnames([prefixCls('clear-icon'), { [prefixCls('clear-icon-show')]: inputValue.length !== 0 }]);
    }, [inputValue.length]);
    return (React.createElement("div", { className: inputWrapCls, style: wrapStyle },
        React.createElement("input", Object.assign({ ref: inputRef, id: id.current, className: inputCls, type: inputType, value: inputValue, autoComplete: autoComplete, onChange: handleChange, onBlur: handleBlur, onCompositionStart: handleComposition, onCompositionEnd: handleComposition }, restProps)),
        allowClear && (React.createElement("label", { className: clearIconCls, htmlFor: id.current },
            React.createElement(Icon, { name: "close-circle", onClick: handleClear })))));
};
export default Input;
